/**
 * Reken- en selectielogica van de productkiezer. Staat los van de dialog zodat
 * de bedragen en de volgorde van een meervoudige selectie te testen zijn zonder
 * de catalogus te laden.
 */
import { isCurrencyAmount, parseCurrencyAmount } from '@/lib/format';
import type { ProductListItem, ProductSnapshot } from '@/types/product';

/** Per product bewaarde keuzes; blijven staan zolang de dialog open is. */
export interface PickerEntry {
  product: ProductListItem;
  snapshot: ProductSnapshot | null;
  variants: Array<{ id: string; name: string }>;
  variantId: string;
  quantity: string;
  discount: string;
  customPrice: string;
  loading: boolean;
}

/** Regeltotaal: een bedrag in centen, of vrije tekst bij "op aanvraag". */
export interface EntryTotal {
  cents: number | null;
  text: string | null;
}

export interface PickedProduct {
  snapshot: ProductSnapshot;
  options: { quantity: number; discountPct: number; customPrice?: string };
}

export const entryQuantity = (entry: PickerEntry): number =>
  Math.max(0, Number(entry.quantity.replace(',', '.')) || 0);

export const entryDiscount = (entry: PickerEntry): number =>
  Math.min(100, Math.max(0, Number(entry.discount.replace(',', '.')) || 0));

export const entryTotal = (entry: PickerEntry): EntryTotal => {
  const quantity = entryQuantity(entry);
  const discount = entryDiscount(entry);
  const custom = entry.customPrice.trim();

  if (custom) {
    if (!isCurrencyAmount(custom)) return { cents: null, text: custom };
    const subtotal = Math.round(parseCurrencyAmount(custom) * 100) * quantity;
    return { cents: Math.round(subtotal - Math.round(subtotal * (discount / 100))), text: null };
  }

  if (!entry.snapshot) return { cents: null, text: null };
  if (entry.snapshot.priceCents === null) {
    // Zonder eigen invoer neemt de offerte de tekst over die bij het product staat.
    const text = entry.snapshot.priceMode === 'on_request' ? entry.snapshot.onRequestText?.trim() : '';
    return { cents: null, text: text || null };
  }
  const subtotal = entry.snapshot.priceCents * quantity;
  return { cents: Math.round(subtotal - Math.round(subtotal * (discount / 100))), text: null };
};

/** Een product telt pas mee als de prijs geladen is en er een aantal staat. */
export const isAddable = (entry: PickerEntry | undefined): entry is PickerEntry =>
  Boolean(entry && entry.snapshot && !entry.loading && entryQuantity(entry) > 0);

/** Telt de aangevinkte producten op; "op maat"-regels blijven apart zichtbaar. */
export const selectionTotal = (entries: PickerEntry[]): { cents: number | null; openItems: number } => {
  let cents = 0;
  let hasAmount = false;
  let openItems = 0;

  for (const entry of entries) {
    const total = entryTotal(entry);
    if (total.cents === null) {
      openItems += 1;
      continue;
    }
    cents += total.cents;
    hasAmount = true;
  }

  return { cents: hasAmount ? cents : null, openItems };
};

/** Vinkt aan of uit; de volgorde van aanvinken is de volgorde van toevoegen. */
export const toggleSelection = (selectedIds: string[], id: string): string[] =>
  selectedIds.includes(id) ? selectedIds.filter((entryId) => entryId !== id) : [...selectedIds, id];

export const withSelection = (selectedIds: string[], id: string): string[] =>
  selectedIds.includes(id) ? selectedIds : [...selectedIds, id];

/** Zet de selectie om naar wat de investeringstabel nodig heeft. */
export const pickedProducts = (entries: PickerEntry[]): PickedProduct[] =>
  entries.map((entry) => {
    const custom = entry.customPrice.trim();
    return {
      snapshot: entry.snapshot as ProductSnapshot,
      options: {
        quantity: entryQuantity(entry),
        discountPct: entryDiscount(entry),
        ...(custom ? { customPrice: custom } : {}),
      },
    };
  });
